import { beginBatchTreeUpdate, endBatchTreeUpdate } from "@/TreeNodes/TreeCache";
import { updateDocumentTitle } from "@/Core/GlobalVars";
let store;
/**
 * Sets up external access to the vuex store.
 *
 * @param  {any} _store  The vuex store.
 */
export function setupExternalStoreAccess(_store) {
    store = _store;
}
/**
 * Sets a state variable.
 *
 * @param  {string} name      The name of the variable to set.
 * @param  {any}    value     The value to set.
 * @param  {string} [module]  The module. Optional. Default is "".
 */
export function setStoreVar(name, value, module = "") {
    if (module === "") {
        store.commit("setVar", { name: name, val: value });
    }
    else {
        store.commit("setVar", {
            name: name,
            val: value,
            module: module,
        });
    }
}
/**
 * Sets the project title in the store and updates the browser tab title.
 *
 * The store watcher also derives document.title from projectTitle, but Vue
 * watchers flush asynchronously. Updating the tab here as well guarantees the
 * rename is reflected immediately and gives renaming a single entry point, so
 * future call sites stay in sync without each remembering to touch the title.
 *
 * @param {string} title  The new project title.
 */
export function setProjectTitle(title) {
    setStoreVar("projectTitle", title);
    updateDocumentTitle(title);
}
/**
 * Adds a value to a list in the state.
 *
 * @param  {string} name   The name of the list to add to.
 * @param  {any}    value  The value to push to the list.
 */
export function pushToStoreList(name, value) {
    store.commit("pushToList", { name: name, val: value });
}
/**
 * Push multiple tree nodes to the store in a single batch, deferring
 * tree cache invalidation until all nodes have been added. This avoids
 * O(n^2) cache recomputation when loading many molecules at once.
 *
 * @param {string}     name    The store variable name (should be "molecules").
 * @param {TreeNode[]} values  The tree nodes to add.
 */
export function pushToStoreListBulk(name, values) {
    beginBatchTreeUpdate();
    try {
        store.commit("pushToMoleculesBulk", values);
    }
    finally {
        endBatchTreeUpdate();
    }
}
/**
 * Gets a store.state variable.
 *
 * @param  {string} name      The name of the variable to get.
 * @param  {string} [module]  The module. Optional. Default is "".
 * @returns {any}  The value of the variable.
 */
function getStoreVar(name, module = "") {
    if (module === "") {
        return store.state[name];
    }
    else {
        return store.state[module][name];
    }
}
/**
 * Gets the molecules from the store. Doing it this way so it will be typed.
 *
 * @returns {TreeNodeList}  The molecules in the store.
 */
export function getMoleculesFromStore() {
    return getStoreVar("molecules");
}
